import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { 
  Globe, 
  Plus, 
  Trash2, 
  MapPin,
  DollarSign,
  Users,
  Shield,
  Eye,
  EyeOff,
  X,
  Check
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Simple Globe placeholder - will render interactive points
const SimpleGlobe = ({ markers, onMarkerClick }) => {
  const canvasRef = useRef(null);
  const [rotation, setRotation] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setRotation(prev => (prev + 0.5) % 360);
    }, 50);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;
    const centerX = width / 2;
    const centerY = height / 2;
    const radius = Math.min(width, height) / 2 - 20;

    // Clear canvas
    ctx.fillStyle = '#050505';
    ctx.fillRect(0, 0, width, height);

    // Draw globe background
    const gradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, radius);
    gradient.addColorStop(0, '#1a1a1a');
    gradient.addColorStop(1, '#0a0a0a');
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
    ctx.fillStyle = gradient;
    ctx.fill();

    // Draw grid lines
    ctx.strokeStyle = 'rgba(220, 20, 60, 0.1)';
    ctx.lineWidth = 1;

    // Latitude lines
    for (let i = -60; i <= 60; i += 30) {
      const y = centerY - (i / 90) * radius;
      const xOffset = Math.sqrt(radius * radius - Math.pow(y - centerY, 2));
      ctx.beginPath();
      ctx.arc(centerX, centerY, xOffset, 0, Math.PI * 2);
      ctx.stroke();
    }

    // Longitude lines
    for (let i = 0; i < 12; i++) {
      const angle = (i * 30 + rotation) * Math.PI / 180;
      ctx.beginPath();
      ctx.ellipse(centerX, centerY, radius * Math.abs(Math.cos(angle)), radius, 0, 0, Math.PI * 2);
      ctx.stroke();
    }

    // Draw markers
    markers.forEach(marker => {
      const lat = marker.lat * Math.PI / 180;
      const lng = (marker.lng + rotation) * Math.PI / 180;
      
      const x = centerX + radius * Math.cos(lat) * Math.sin(lng);
      const y = centerY - radius * Math.sin(lat);
      
      // Only draw if on visible side
      if (Math.cos(lng) > -0.2) {
        // Glow effect
        const glowGradient = ctx.createRadialGradient(x, y, 0, x, y, 15);
        glowGradient.addColorStop(0, marker.status === 'active' ? 'rgba(220, 20, 60, 0.8)' : 'rgba(255, 215, 0, 0.8)');
        glowGradient.addColorStop(1, 'rgba(220, 20, 60, 0)');
        ctx.beginPath();
        ctx.arc(x, y, 15, 0, Math.PI * 2);
        ctx.fillStyle = glowGradient;
        ctx.fill();

        // Point
        ctx.beginPath();
        ctx.arc(x, y, 4, 0, Math.PI * 2);
        ctx.fillStyle = marker.status === 'active' ? '#DC143C' : '#FFD700';
        ctx.fill();
      }
    });

  }, [rotation, markers]);

  return (
    <canvas 
      ref={canvasRef} 
      width={400} 
      height={400}
      className="mx-auto"
    />
  );
};

export const JardinSecretModule = ({ token }) => {
  const [profiles, setProfiles] = useState([]);
  const [trackerData, setTrackerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [newProfile, setNewProfile] = useState({
    name: '',
    codename: '',
    location: '',
    coordinates: { lat: 0, lng: 0 },
    status: 'active',
    revenue: 0,
    notes: ''
  });

  const fetchData = useCallback(async () => {
    try {
      const [profilesRes, trackerRes] = await Promise.all([
        axios.get(`${API}/garden/profiles`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/garden/tracker`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setProfiles(profilesRes.data);
      setTrackerData(trackerRes.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Accès refusé - Admin uniquement');
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [token, fetchData]);

  const createProfile = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${API}/garden/profiles`,
        newProfile,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Profil créé avec succès');
      setShowForm(false);
      setNewProfile({
        name: '',
        codename: '',
        location: '',
        coordinates: { lat: 0, lng: 0 },
        status: 'active',
        revenue: 0,
        notes: ''
      });
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la création');
    }
  };

  const deleteProfile = async (id) => {
    if (!window.confirm('Supprimer ce profil ?')) return;
    try {
      await axios.delete(`${API}/garden/profiles/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Profil supprimé');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-[#00FF9D] font-mono text-sm animate-pulse">
          DÉCHIFFREMENT DES DONNÉES SECRÈTES...
        </div>
      </div>
    );
  }

  return (
    <div data-testid="jardin-secret-module" className="space-y-6 animate-fade-in">
      {/* Warning Banner */}
      <div className="grid-card border-[#00FF9D]/50 bg-[#00FF9D]/5 p-4">
        <div className="flex items-center gap-3">
          <Shield className="w-5 h-5 text-[#00FF9D]" />
          <div>
            <p className="font-heading text-sm text-[#00FF9D] uppercase">JARDIN SECRET - ZONE SÉCURISÉE</p>
            <p className="font-mono text-xs text-[#888888]">
              Données confidentielles • Accès admin uniquement • Pas de traces externes
            </p>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="stat-card border-l-2 border-l-[#00FF9D]">
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-4 h-4 text-[#00FF9D]" />
            <span className="font-heading text-xs uppercase tracking-widest text-[#888888]">
              PROFILS ACTIFS
            </span>
          </div>
          <p className="font-mono text-3xl font-bold text-[#EDEDED]">
            {trackerData?.total_profiles || 0}
          </p>
        </div>

        <div className="stat-card border-l-2 border-l-[#DC143C]">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="w-4 h-4 text-[#DC143C]" />
            <span className="font-heading text-xs uppercase tracking-widest text-[#888888]">
              REVENUS SECRETS
            </span>
          </div>
          <p className="font-mono text-3xl font-bold text-[#EDEDED]">
            €{(trackerData?.total_revenue || 0).toLocaleString()}
          </p>
        </div>

        <div className="stat-card border-l-2 border-l-[#00BFFF]">
          <div className="flex items-center gap-2 mb-2">
            <Globe className="w-4 h-4 text-[#00BFFF]" />
            <span className="font-heading text-xs uppercase tracking-widest text-[#888888]">
              LOCALISATIONS
            </span>
          </div>
          <p className="font-mono text-3xl font-bold text-[#EDEDED]">
            {trackerData?.markers?.length || 0}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Global Tracker */}
        <div className="grid-card">
          <div className="module-header">
            <div className="flex items-center gap-3">
              <Globe className="w-4 h-4 text-[#00FF9D]" />
              <span className="module-title">GLOBAL TRACKER</span>
            </div>
            <span className="module-badge">LIVE</span>
          </div>
          <div className="p-4 globe-container flex items-center justify-center min-h-[420px]">
            <SimpleGlobe 
              markers={trackerData?.markers || []}
              onMarkerClick={(marker) => toast.info(`${marker.name} - ${marker.status}`)}
            />
          </div>
        </div>

        {/* Profiles List */}
        <div className="grid-card flex flex-col">
          <div className="module-header">
            <div className="flex items-center gap-3">
              <Users className="w-4 h-4 text-[#DC143C]" />
              <span className="module-title">PROFILS</span>
            </div>
            <button
              data-testid="add-profile-btn"
              onClick={() => setShowForm(!showForm)}
              className="module-badge cursor-pointer hover:bg-[#DC143C]/40 transition-colors flex items-center gap-2"
            >
              {showForm ? <X className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
              {showForm ? 'ANNULER' : 'AJOUTER'}
            </button>
          </div>

          {/* Add Form */}
          {showForm && (
            <form onSubmit={createProfile} className="p-4 border-b border-white/10 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <input
                  data-testid="profile-name-input"
                  type="text"
                  placeholder="Nom"
                  value={newProfile.name}
                  onChange={(e) => setNewProfile({...newProfile, name: e.target.value})}
                  className="tactical-input text-sm"
                  required
                />
                <input
                  data-testid="profile-codename-input"
                  type="text"
                  placeholder="Codename"
                  value={newProfile.codename}
                  onChange={(e) => setNewProfile({...newProfile, codename: e.target.value})}
                  className="tactical-input text-sm"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input
                  data-testid="profile-location-input"
                  type="text"
                  placeholder="Location"
                  value={newProfile.location}
                  onChange={(e) => setNewProfile({...newProfile, location: e.target.value})}
                  className="tactical-input text-sm"
                  required
                />
                <input
                  data-testid="profile-revenue-input"
                  type="number"
                  placeholder="Revenue"
                  value={newProfile.revenue}
                  onChange={(e) => setNewProfile({...newProfile, revenue: parseFloat(e.target.value) || 0})}
                  className="tactical-input text-sm"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input
                  type="number"
                  placeholder="Latitude"
                  value={newProfile.coordinates.lat}
                  onChange={(e) => setNewProfile({
                    ...newProfile, 
                    coordinates: {...newProfile.coordinates, lat: parseFloat(e.target.value) || 0}
                  })}
                  className="tactical-input text-sm"
                  step="0.001"
                />
                <input
                  type="number"
                  placeholder="Longitude"
                  value={newProfile.coordinates.lng}
                  onChange={(e) => setNewProfile({
                    ...newProfile, 
                    coordinates: {...newProfile.coordinates, lng: parseFloat(e.target.value) || 0}
                  })}
                  className="tactical-input text-sm"
                  step="0.001"
                />
              </div>
              <textarea
                placeholder="Notes confidentielles..."
                value={newProfile.notes}
                onChange={(e) => setNewProfile({...newProfile, notes: e.target.value})}
                className="tactical-input text-sm w-full h-20 resize-none"
              />
              <button
                data-testid="submit-profile-btn"
                type="submit"
                className="w-full btn-primary py-2 text-sm flex items-center justify-center gap-2"
              >
                <Check className="w-4 h-4" />
                CRÉER PROFIL
              </button>
            </form>
          )}

          {/* Profiles */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {profiles.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-12 h-12 text-[#333333] mx-auto mb-4" />
                <p className="font-mono text-sm text-[#888888]">
                  Aucun profil enregistré
                </p>
              </div>
            ) : (
              profiles.map((profile, index) => (
                <div 
                  key={profile.id || index}
                  className="p-4 bg-[#050505] border border-white/10 hover:border-[#00FF9D]/50 transition-colors"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h4 className="font-heading text-sm font-bold text-[#EDEDED] uppercase">
                        {profile.codename}
                      </h4>
                      <p className="font-mono text-xs text-[#888888]">{profile.name}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${
                        profile.status === 'active' ? 'status-online' : 'status-standby'
                      }`} />
                      <button
                        onClick={() => deleteProfile(profile.id)}
                        className="p-1 hover:bg-[#DC143C]/20 transition-colors"
                      >
                        <Trash2 className="w-4 h-4 text-[#DC143C]" />
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-xs">
                    <div className="flex items-center gap-1 text-[#888888]">
                      <MapPin className="w-3 h-3" />
                      {profile.location}
                    </div>
                    <div className="flex items-center gap-1 text-[#00FF9D]">
                      <DollarSign className="w-3 h-3" />
                      €{profile.revenue?.toLocaleString()}
                    </div>
                  </div>
                  {profile.notes && (
                    <p className="font-mono text-xs text-[#444444] mt-2 truncate">
                      {profile.notes}
                    </p>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default JardinSecretModule;

